import { userInfoSafe } from 'hub-http/userInfo';
import { Logger } from '../devtools/logger';
import { ChangeContext } from '../client/changeManager';

export const VISIBILITY_GRACE_MS = 30_000;
export const VISIBILITY_GATE = 'Pulse:VisibilityHandling';

let __TEST_ONLY_DISABLED = false;
let gateCheck: Promise<boolean> | undefined;

export const __testOnlyDisableVisibilityHandling = () => {
  __TEST_ONLY_DISABLED = true;
};

export const __testOnlyEnableVisibilityHandling = () => {
  __TEST_ONLY_DISABLED = false;
  gateCheck = undefined;
};

const isGateEnabled = () => {
  gateCheck ??= userInfoSafe().then(
    ({ gates }) => gates.includes(VISIBILITY_GATE),
    () => false
  );

  return gateCheck;
};

export const createVisibilityManager = ({
  logger,
  getAttachedKeys,
  isSettled,
  hasData,
  isExpired,
  isDefinitionKeptAlive,
  refresh,
  suspend,
  resume,
}: {
  logger?: Logger;
  getAttachedKeys: () => Iterable<string>;
  isSettled: (instanceKey: string) => boolean;
  hasData: (instanceKey: string) => boolean;
  isExpired: (instanceKey: string) => boolean;
  isDefinitionKeptAlive: (instanceKey: string) => boolean;
  refresh: (instanceKey: string, changeContext?: ChangeContext) => void;
  suspend: (instanceKey: string) => void;
  resume: (instanceKey: string) => void;
}) => {
  let hidden = false;
  let graceTimer: ReturnType<typeof setTimeout> | undefined;

  const queued = new Map<string, ChangeContext | undefined>();
  const keepAliveCounts = new Map<string, number>();

  const isKeptAlive = (instanceKey: string) =>
    keepAliveCounts.has(instanceKey) || isDefinitionKeptAlive(instanceKey);

  const suspendAll = () => {
    for (const instanceKey of getAttachedKeys()) {
      if (!isKeptAlive(instanceKey) && isSettled(instanceKey)) {
        suspend(instanceKey);
      }
    }

    logger?.debug('suspended resolvers');
  };

  const onHidden = () => {
    hidden = true;
    graceTimer = setTimeout(suspendAll, VISIBILITY_GRACE_MS);

    logger?.debug('page hidden');
  };

  const onVisible = () => {
    hidden = false;
    clearTimeout(graceTimer);

    const toRefresh = new Map(queued);
    queued.clear();

    for (const instanceKey of getAttachedKeys()) {
      resume(instanceKey);

      if (!toRefresh.has(instanceKey) && isExpired(instanceKey)) {
        toRefresh.set(instanceKey, undefined);
      }
    }

    logger?.debug('page visible', { refreshing: toRefresh.size });

    for (const [instanceKey, changeContext] of toRefresh) {
      refresh(instanceKey, changeContext);
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'hidden') {
      if (hidden) {
        onVisible();
      }
      return;
    }

    if (__TEST_ONLY_DISABLED || hidden) {
      return;
    }

    void isGateEnabled().then((gateEnabled) => {
      if (gateEnabled && document.visibilityState === 'hidden' && !hidden) {
        onHidden();
      }
    });
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  return {
    requestRefresh: (instanceKey: string, changeContext?: ChangeContext) => {
      if (hidden && !isKeptAlive(instanceKey) && hasData(instanceKey)) {
        logger?.debug('queueing refresh while hidden', { instanceKey });

        queued.set(instanceKey, changeContext);
        return;
      }

      refresh(instanceKey, changeContext);
    },
    keepAlive: (instanceKey: string) => {
      keepAliveCounts.set(
        instanceKey,
        (keepAliveCounts.get(instanceKey) ?? 0) + 1
      );
      resume(instanceKey);

      if (queued.has(instanceKey)) {
        refresh(instanceKey, queued.get(instanceKey));
        queued.delete(instanceKey);
      }

      return () => {
        const remaining = keepAliveCounts.get(instanceKey)! - 1;

        if (remaining > 0) {
          keepAliveCounts.set(instanceKey, remaining);
        } else {
          keepAliveCounts.delete(instanceKey);
        }
      };
    },
    destroy: () => {
      clearTimeout(graceTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
};

export type VisibilityManager = ReturnType<typeof createVisibilityManager>;
